import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import Tooltip from "../Tooltip";
import LocalAIAnalyst from "./LocalAIAnalyst";
import IntentRadar from "./IntentRadar";
import {
  GovernedLocalEntityMention,
  GovernedLocalEntityRole,
  toLocalCompetitorRadarSeries,
  toLocalCustomerRadarSeries,
} from "./localIntentRadarAdapter";
import { loadLocalCompetitorRadarData } from "./localCompetitorRadarData";
import { formatRecommendationScoreOutOfTen } from "../../lib/localRecommendationStrength";
import { formatLocalIntentLabel } from "../../lib/localIntentLabels";
import { getLocalVisibilityLabel } from "../../lib/localVisibilityLabel";
import { formatPercentage } from "../../lib/percentageFormatting";

type LocalSummary = {
  account_id: string;
  run_id: string;
  total_results: number | null;
  organic_results: number | null;
  organic_business_mentions: number | null;
  organic_local_visibility_rate: string | number | null;
  comparison_results: number | null;
  comparison_business_mentions: number | null;
  comparison_visibility_rate: string | number | null;
  avg_prompt_score: string | number | null;
  avg_confidence_level: string | number | null;
  estimated_run_cost: string | number | null;
};

type LocalEntity = {
  entity_name: string;
  business_name?: string | null;
  is_business_entity?: boolean | null;
  mention_rows: number | null;
  organic_mention_rows: number | null;
  comparison_mention_rows: number | null;
  avg_prompt_score: string | number | null;
  avg_confidence_level: string | number | null;
};

type LocalCategory = {
  account_id?: string;
  run_id?: string;
  local_prompt_category: string;
  result_count: number | null;
  organic_result_count: number | null;
  organic_business_mentions: number | null;
  organic_visibility_rate: string | number | null;
  avg_prompt_score: string | number | null;
  avg_confidence_level: string | number | null;
  strong_signal_count: number | null;
  weak_signal_count: number | null;
  error_count: number | null;
};

type LocalVisibilityProps = {
  accountId: string | null;
  runId?: string | null;
  isAdminView?: boolean;
  historyComparison?: { state: string; message: string } | null;
  runFreshnessLabel?: string;
};

function getStrongestIntentCategory(
  rows: Array<{ label?: string | null; category?: string | null; score?: number | null; averageScore?: number | null; currentScore?: number | null }>
) {
  const scoredRows = rows
    .map((row) => {
      const rawScore = row.currentScore ?? row.averageScore ?? row.score;
      const score = rawScore == null ? Number.NaN : Number(rawScore);

      return {
        label: row.label || row.category || "Unknown",
        score,
      };
    })
    .filter((row) => Number.isFinite(row.score));

  if (scoredRows.length === 0) {
    return null;
  }

  return scoredRows.sort((a, b) => b.score - a.score)[0];
}

function toNumber(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toNullableNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatPercent(value: string | number | null | undefined): string {
  const parsed = toNullableNumber(value);
  return formatPercentage(parsed);
}

function formatScoreOutOfTen(value: string | number | null | undefined): string {
  return formatRecommendationScoreOutOfTen(value, "score_out_of_ten");
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 22,
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.04)"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
    marginBottom: 20
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#155e75",
    margin: 0
  },
  title: {
    fontSize: 22,
    lineHeight: 1.2,
    fontWeight: 800,
    color: "#0f172a",
    margin: "6px 0 0"
  },
  narrative: {
    maxWidth: 760,
    fontSize: 13,
    lineHeight: 1.55,
    color: "#475569",
    margin: "8px 0 0"
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 20
  },
  metricCard: {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 16
  },
  metricLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#64748b",
    margin: 0
  },
  metricValue: {
    fontSize: 30,
    fontWeight: 850,
    color: "#0f172a",
    margin: "10px 0 0"
  },
  metricValueText: {
  marginTop: 8,
  fontSize: 22,
  fontWeight: 900,
  color: "#0f172a",
  lineHeight: 1.15,
},
  metricSubtext: {
    fontSize: 12,
    color: "#64748b",
    margin: "6px 0 0"
  },
  twoColumnGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 16,
    marginTop: 18
  },
  panel: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 16,
    background: "#ffffff"
  },
  panelTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: "#0f172a",
    margin: 0
  },
  panelSubtitle: {
    fontSize: 12,
    color: "#64748b",
    margin: "6px 0 14px"
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid #f1f5f9"
  },
  rowTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#0f172a",
    margin: 0
  },
  rowMeta: {
    fontSize: 12,
    color: "#64748b",
    margin: "4px 0 0"
  },
  rowNumber: {
    fontSize: 16,
    fontWeight: 850,
    color: "#0f172a",
    margin: 0,
    textAlign: "right"
  },
  rowNumberLabel: {
    fontSize: 11,
    color: "#64748b",
    margin: 0,
    textAlign: "right"
  },
  recommendation: {
    marginTop: 18,
    background: "#0f172a",
    color: "#ffffff",
    borderRadius: 12,
    padding: 16
  },
  recommendationTitle: {
    fontSize: 14,
    fontWeight: 800,
    margin: 0
  },
  recommendationText: {
    fontSize: 13,
    lineHeight: 1.55,
    color: "#cbd5e1",
    margin: "6px 0 0"
  }
};

export default function LocalVisibility({
  accountId,
  runId = null,
  isAdminView = false,
  historyComparison = null,
  runFreshnessLabel,
}: LocalVisibilityProps) {
  const [summary, setSummary] = useState<LocalSummary | null>(null);
  const [entities, setEntities] = useState<LocalEntity[]>([]);
  const [categories, setCategories] = useState<LocalCategory[]>([]);
  const [competitorRoles, setCompetitorRoles] = useState<GovernedLocalEntityRole[]>([]);
  const [competitorMentions, setCompetitorMentions] = useState<GovernedLocalEntityMention[]>([]);
  const [competitorRadarNotice, setCompetitorRadarNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [leaderboardViewMode, setLeaderboardViewMode] = useState<"table" | "chart">("table");
  const summaryMatchesRequest = summary?.account_id === accountId && summary?.run_id === runId;

  useEffect(() => {
    let cancelled = false;

    async function loadLocalVisibility() {
      if (!accountId || !runId) {
        setErrorMessage("");
        setSummary(null);
        setEntities([]);
        setCategories([]);
        setCompetitorRoles([]);
        setCompetitorMentions([]);
        setCompetitorRadarNotice("");
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage("");
      setSummary(null);
      setEntities([]);
      setCategories([]);
      setCompetitorRoles([]);
      setCompetitorMentions([]);
      setCompetitorRadarNotice("");

      try {
        const { data: summaryData, error: summaryError } = await supabase
          .from("toren_local_summary")
          .select("*")
          .eq("account_id", accountId)
          .eq("run_id", runId)
          .order("latest_executed_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (summaryError) throw summaryError;

        if (!summaryData?.run_id) {
          if (!cancelled) {
            setSummary(null);
            setEntities([]);
            setCategories([]);
            setCompetitorRoles([]);
            setCompetitorMentions([]);
          }
          return;
        }

        const [
          { data: entityData, error: entityError },
          { data: categoryData, error: categoryError },
          competitorResult,
        ] = await Promise.all([
          supabase
            .from("toren_local_top_entities")
            .select("*")
            .eq("account_id", accountId)
            .eq("run_id", summaryData.run_id)
            .gt("organic_mention_rows", 0)
            .order("organic_mention_rows", { ascending: false })
            .order("entity_name", { ascending: true })
            .limit(8),

          supabase
            .from("toren_local_category_performance")
            .select("*")
            .eq("account_id", accountId)
            .eq("run_id", summaryData.run_id)
            .order("organic_visibility_rate", {
              ascending: true,
              nullsFirst: false
            }),

          loadLocalCompetitorRadarData(supabase, accountId, summaryData.run_id)
            .then((data) => ({ data, error: null }))
            .catch((error) => ({ data: null, error }))
        ]);

        if (entityError) throw entityError;
        if (categoryError) throw categoryError;

        if (!cancelled) {
          setSummary(summaryData as LocalSummary);
          setEntities((entityData || []) as LocalEntity[]);
          setCategories((categoryData || []) as LocalCategory[]);
          setCompetitorRoles((competitorResult.data?.roles || []) as GovernedLocalEntityRole[]);
          setCompetitorMentions((competitorResult.data?.mentions || []) as GovernedLocalEntityMention[]);
          if (competitorResult.error) {
            console.warn("Local competitor radar evidence is unavailable.");
            setCompetitorRadarNotice("Governed competitor overlays are unavailable for this run; customer measurements remain available.");
          }
        }
      } catch (error: any) {
        console.error("Local visibility load error:", error);
        if (!cancelled) {
          setErrorMessage(
            error?.message || "Unable to load local visibility data."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadLocalVisibility();
    return () => {
      cancelled = true;
    };
  }, [accountId, runId]);

  function getLeaderboardToggleStyle(mode: "table" | "chart"): React.CSSProperties {
    const active = leaderboardViewMode === mode;

    return {
      border: "1px solid " + (active ? "#0891b2" : "#dbe3ef"),
      background: active ? "#0891b2" : "#ffffff",
      color: active ? "#ffffff" : "#64748b",
      borderRadius: 999,
      padding: "6px 12px",
      fontSize: 13,
      fontWeight: 800,
      cursor: "pointer"
    };
  }

  const narrative = useMemo(() => {
    if (!summary) return "";

    const organicVisibility = toNullableNumber(summary.organic_local_visibility_rate);
    const comparisonVisibility = toNullableNumber(summary.comparison_visibility_rate);

    if (organicVisibility === null) {
      return "Organic visibility is unavailable for this run.";
    }

    if (organicVisibility === 0 && comparisonVisibility !== null && comparisonVisibility > 0) {
      return `This run recorded no organic response presence; the business appeared in ${formatPercentage(comparisonVisibility)} of comparison-category responses.`;
    }

    if (organicVisibility > 0 && organicVisibility < 25) {
      return `This business appeared in ${formatPercentage(organicVisibility)} of eligible organic Local responses in this run.`;
    }

    if (organicVisibility >= 25) {
      return "This run recorded measurable organic response presence across Local search prompts.";
    }

    return "Toren is preparing the latest local AI visibility summary for this account.";
  }, [summary]);
  const visibleCategories = categories.filter(
  (category) =>
    !["local_comparison", "dealer_comparison"].includes(
      category.local_prompt_category
    )
);
const localRadarSeries = toLocalCustomerRadarSeries(visibleCategories, {
  accountId: summary?.account_id,
  runId: summary?.run_id,
});
const competitorRadarSeries = localRadarSeries && summary
  ? toLocalCompetitorRadarSeries(
      localRadarSeries,
      visibleCategories,
      competitorMentions,
      competitorRoles,
      { accountId: summary.account_id, runId: summary.run_id }
    )
  : [];
const measuredIntentCount = visibleCategories.filter(
  (category) => toNullableNumber(category.organic_visibility_rate) !== null
).length;
const coveredIntentCount = visibleCategories.filter((category) => {
  const visibility = toNullableNumber(category.organic_visibility_rate);
  return visibility !== null && visibility > 0;
}).length;
const strongestIntentCategory =
  visibleCategories.some((category) => toNullableNumber(category.avg_prompt_score) !== null)
    ? visibleCategories
        .filter((category) => toNullableNumber(category.avg_prompt_score) !== null)
        .sort((a, b) => toNumber(b.avg_prompt_score) - toNumber(a.avg_prompt_score))[0]
    : null;

const organicVisibility = toNullableNumber(summary?.organic_local_visibility_rate);
const organicVisibilityLabel = getLocalVisibilityLabel(organicVisibility);

if (loading || (Boolean(accountId && runId && summary) && !summaryMatchesRequest)) {
  return (
    <section style={styles.section}>
      <p style={styles.panelTitle}>Loading local AI visibility…</p>
    </section>
  );
}

if (errorMessage) {
  return (
    <section
      style={{
        ...styles.section,
        borderColor: "#fecaca",
        background: "#fef2f2"
      }}
    >
      <p style={{ ...styles.panelTitle, color: "#991b1b" }}>
        Local visibility data could not be loaded.
      </p>
      <p style={{ ...styles.panelSubtitle, color: "#b91c1c" }}>
        {errorMessage}
      </p>
    </section>
  );
}

if (!summary) {
  return (
    <section style={styles.section}>
      <p style={styles.panelTitle}>Local AI Visibility</p>
      <p style={styles.panelSubtitle}>
        No local run data is available yet for this account.
      </p>
    </section>
  );
}

return (
  <>
  <div data-testid="local-ai-analyst-brief">
    <LocalAIAnalyst
      organicVisibility={toNullableNumber(summary.organic_local_visibility_rate)}
      recommendationStrengthOutOfTen={toNullableNumber(summary.avg_prompt_score)}
      strongestIntent={
        getStrongestIntentCategory(visibleCategories.map((category) => ({
          label: category.local_prompt_category,
          score: toNullableNumber(category.avg_prompt_score)
        })))?.label ?? null
      }
      strongestIntentScoreOutOfTen={
        getStrongestIntentCategory(visibleCategories.map((category) => ({
          label: category.local_prompt_category,
          score: toNullableNumber(category.avg_prompt_score)
        })))?.score ?? null
      }
      weakestIntent={
        visibleCategories
          .map((category) => ({ label: category.local_prompt_category, score: toNullableNumber(category.avg_prompt_score) }))
          .filter((category) => category.label && category.score !== null)
          .sort((a, b) => (a.score as number) - (b.score as number))[0]?.label ?? null
      }
      historicalTrend={historyComparison || { state: "loading", message: "Validating historical results before reporting a trend." }}
      runFreshnessLabel={runFreshnessLabel}
      recommendedNextMove={null}
    />

  </div>
  <section data-testid="local-ai-visibility" style={styles.section}>
    <div style={styles.header}>
      <div>
        <p style={styles.eyebrow}>Toren Local</p>
        <h2 style={styles.title}>Local AI Visibility</h2>
        <p style={styles.narrative}>{narrative}</p>
      </div>

    </div>

    <div style={styles.metricGrid}>
      <div style={styles.metricCard}>
        <p style={styles.metricLabel}>
              Organic Visibility{" "}
              <Tooltip text="Share of eligible non-comparison Local responses that include this business under the existing entity-mention measurement." />
            </p>
        <p style={styles.metricValue}>
          {formatPercent(summary.organic_local_visibility_rate)}
        </p>
        <p style={styles.metricSubtext}>
          {summary.organic_business_mentions == null || summary.organic_results == null
            ? "Response counts unavailable"
            : `${summary.organic_business_mentions} of ${summary.organic_results} eligible organic Local responses`} · {organicVisibilityLabel}
        </p>
      </div>

      <div style={styles.metricCard}>
  <p style={styles.metricLabel}>
              Recommendation Strength{" "}
              <Tooltip text="Average prompt score for this Local run, displayed on the existing 0–10 scale." />
            </p>
        <p style={styles.metricValue}>
    {formatScoreOutOfTen(summary.avg_prompt_score)}
  </p>
  <p style={styles.metricSubtext}>
    {summary.avg_prompt_score == null ? "Recommendation strength unavailable" : "Average prompt score on the Local 0–10 scale"}
  </p>
</div>

      <div style={styles.metricCard}>
        <p style={styles.metricLabel}>
              Intent Coverage{" "}
              <Tooltip text="Shows how many local search intent groups include this business in AI responses." />
            </p>
        <p style={styles.metricValue}>
          {measuredIntentCount > 0 ? `${coveredIntentCount} of ${measuredIntentCount}` : "—"}
        </p>
        <p style={styles.metricSubtext}>
    {measuredIntentCount > 0 ? "Measured Local intent groups with visibility" : "Intent group measurements unavailable"}
        </p>
      </div>

      <div style={styles.metricCard}>
  <p style={styles.metricLabel}>
              Highest Average Prompt Score by Intent{" "}
              <Tooltip text="Intent group with the highest available average prompt_score in this Local run; this is not the visibility ranking." />
            </p>
  <p style={styles.metricValueText}>
    {strongestIntentCategory
      ? formatLocalIntentLabel(strongestIntentCategory.local_prompt_category)
      : "—"}
  </p>
  <p style={styles.metricSubtext}>
    {strongestIntentCategory
      ? `${formatScoreOutOfTen(strongestIntentCategory.avg_prompt_score)} recommendation score`
      : "Available after analysis"}
  </p>
</div>
    </div>

    <IntentRadar
      title="Local Intent Visibility"
      description="Your business and approved competitors’ organic visibility across eligible Local responses for this run. Every axis uses the same 0–100% scale. Competitor recommendation strength is not inferred."
      series={localRadarSeries ? [localRadarSeries, ...competitorRadarSeries] : []}
      status={localRadarSeries ? "ready" : "empty"}
    />
    {localRadarSeries && competitorRadarSeries.length === 0 && !competitorRadarNotice && (
      <p role="status" style={{ ...styles.panelSubtitle, marginTop: 8 }}>
        Competitor comparisons could not be verified for this run. Customer measurements remain available.
      </p>
    )}
    {competitorRadarNotice && <p role="status" style={{ ...styles.panelSubtitle, marginTop: 8 }}>{competitorRadarNotice}</p>}

  </section>

  <section data-testid="local-competitive-intelligence" aria-labelledby="local-competitive-heading" style={{ ...styles.section, marginTop: 16, padding: 18, boxShadow: "none" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
      <div>
        <p style={styles.eyebrow}>Existing Local measurement</p>
        <h2 id="local-competitive-heading" style={{ ...styles.title, fontSize: 20 }}>Competitive Intelligence</h2>
        <p style={{ ...styles.narrative, marginTop: 5 }}>The leaderboard below summarizes business mentions across completed Local prompts. It is separate from future profile-scoped governed competitor measurements.</p>
      </div>
      <span style={{ border: "1px solid #d1d5db", borderRadius: 999, padding: "5px 9px", color: "#475569", fontSize: 11, fontWeight: 750, background: "#f8fafc" }}>Existing Local AI Leaderboard</span>
    </div>
    <div style={styles.twoColumnGrid}>
      <div style={styles.panel}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            marginBottom: 12
          }}
        >
          <div>
            <p style={styles.panelTitle}>
              Existing Local AI Leaderboard{" "}
              <Tooltip text="Ranks businesses by their appearance in eligible organic Local responses. This does not measure recommendation strength." />
            </p>
            <p style={styles.panelSubtitle}>
              Ranks entities by eligible organic Local responses where they appear. Response presence does not measure recommendation strength or customer demand.
            </p>
          </div>

          <div data-pdf-control-group="leaderboard-view" style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button
              type="button"
              aria-pressed={leaderboardViewMode === "table"}
              onClick={() => setLeaderboardViewMode("table")}
              style={getLeaderboardToggleStyle("table")}
            >
              Table
            </button>
            <button
              type="button"
              aria-pressed={leaderboardViewMode === "chart"}
              onClick={() => setLeaderboardViewMode("chart")}
              style={getLeaderboardToggleStyle("chart")}
            >
              Chart
            </button>
          </div>
        </div>

        {leaderboardViewMode === "chart" ? (
          <div style={{ display: "grid", gap: 10 }}>
            {entities.map((entity) => {
              const organicMentions = toNullableNumber(entity.organic_mention_rows);
              const organicPromptTotal = toNullableNumber(summary.organic_results);
              const visibility = organicMentions !== null && organicPromptTotal !== null && organicPromptTotal > 0
                ? (organicMentions / organicPromptTotal) * 100
                : null;
              const isCustomer = Boolean(entity.is_business_entity);

              return (
                <div key={entity.entity_name}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      marginBottom: 5
                    }}
                  >
                    <div
                      style={{
                        color: "#0f172a",
                        fontWeight: isCustomer ? 800 : 700,
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap"
                      }}
                    >
                      <span>{entity.entity_name}</span>
                      {isCustomer && (
                        <span
                          style={{
                            ...styles.badge,
                            borderRadius: 12,
                            background: "#ecfeff",
                            color: "#155e75",
                            border: "1px solid #bae6fd",
                            padding: "3px 8px",
                            fontSize: 11,
                            fontWeight: 800,
                            verticalAlign: "middle",
                            whiteSpace: "nowrap"
                          }}
                        >
                          Your business
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        color: "#0f172a",
                        fontWeight: 800,
                        fontSize: 14
                      }}
                    >
                      {formatPercentage(visibility)}
                    </div>
                  </div>

                  <div
                    style={{
                      height: 10,
                      borderRadius: 999,
                      background: "#e2e8f0",
                      overflow: "hidden"
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.max(0, Math.min(100, visibility ?? 0))}%`,
                        borderRadius: 999,
                        background: isCustomer ? "#0891b2" : "#93c5fd"
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {entities.map((entity) => {
              const organicMentions = toNullableNumber(entity.organic_mention_rows);
              const organicPromptTotal = toNullableNumber(summary.organic_results);
              const visibility = organicMentions !== null && organicPromptTotal !== null && organicPromptTotal > 0
                ? (organicMentions / organicPromptTotal) * 100
                : null;

              return (
                <div key={entity.entity_name} style={styles.row}>
                  <div>
                    <p style={styles.rowTitle}>
                      {entity.entity_name}
                      {entity.is_business_entity && (
                        <span
                          style={{
                            ...styles.badge,
                            marginLeft: 8,
                            borderRadius: 12,
                            background: "#ecfeff",
                            color: "#155e75",
                            border: "1px solid #bae6fd",
                            padding: "3px 8px",
                            fontSize: 11,
                            fontWeight: 800,
                            verticalAlign: "middle",
                            whiteSpace: "nowrap"
                          }}
                        >
                          Your business
                        </span>
                      )}
                    </p>
                    <p style={styles.rowMeta}>
                      {entity.is_business_entity
                        ? "Your business in local AI results"
                        : "Competitor in local AI results"}
                    </p>
                  </div>

                  <div title={organicMentions === null || organicPromptTotal === null ? "Mention measurement unavailable" : `${organicMentions} of ${organicPromptTotal} eligible organic Local responses`}>
                    <p style={styles.rowNumber}>{formatPercentage(visibility)}</p>
                    <p style={styles.rowNumberLabel}>response presence</p>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

    </div>

    {!isAdminView && (
      <div style={styles.recommendation}>
        <p style={styles.recommendationTitle}>Recommended next step</p>
        <p style={styles.recommendationText}>
          Review the Local prompts behind these response-presence counts and compare the response context before choosing a content change.
        </p>
      </div>
    )}
  </section>
  </>
);
}
